import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Lock, OctagonAlert, Save, TriangleAlert, Upload } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useDebounce } from '@/hooks/useDebounce';
import { captureError } from '@/lib/logger';
import { parseAgreementError } from '@/lib/agreements/constants';
import { collectMergeFields } from '@/lib/agreements/merge';
import { fetchAgreementTemplateBody, updateTemplateDraft } from '@/lib/agreements/templates';
import {
    SPECIMEN_MERGE_DATA,
    malformedMergeFields,
    unresolvableMergeFields,
    type AgreementTemplateBody,
} from '@/lib/agreements/templateCatalog';

import AgreementMarkdown from './AgreementMarkdown';

interface TemplateEditorSheetProps {
    /** The version being opened. Null closes the sheet. */
    templateId: number | null;
    onOpenChange: (open: boolean) => void;
    /** Opens the publish confirmation for the draft on screen. */
    onPublish: (template: AgreementTemplateBody) => void;
}

/** How long the typing has to settle before the preview re-renders the whole document. */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The markdown editor and its live preview.
 *
 * A PUBLISHED VERSION OPENS HERE TOO, READ-ONLY. The immutability trigger seals a template
 * the moment it is published or referenced, so there is no such thing as editing v1 — but
 * reading it is exactly what an admin needs before deciding what v2 should say, and the
 * version history's only useful action would otherwise be nothing at all.
 *
 * THE PREVIEW IS SPECIMEN DATA AND SAYS SO. No contract is in play while a template is being
 * written, so the pane merges `SPECIMEN_MERGE_DATA` with highlighting permanently on: every
 * substituted run is tinted, the banner names it, and the invented names carry the word
 * "Specimen". It is a check on wording, layout and column widths — never a document.
 */
export default function TemplateEditorSheet({
    templateId,
    onOpenChange,
    onPublish,
}: TemplateEditorSheetProps) {
    const queryClient = useQueryClient();

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    const { data: template, isLoading } = useQuery({
        queryKey: ['agreement-template-body', templateId],
        queryFn: () => fetchAgreementTemplateBody(templateId as number),
        enabled: !!templateId,
    });

    /**
     * Seeded from the row rather than held in the query cache: this is an editor, so the
     * fetched values are the STARTING POINT and every later keystroke belongs to the form.
     *
     * ONCE PER OPENING, NOT ONCE PER FETCH. The ref is what makes "discard" mean discard —
     * closing clears it, so reopening the same version re-seeds from the database instead of
     * showing the edits that were just thrown away — while a refetch of the row already on
     * screen (the invalidation a save fires) leaves the form alone rather than racing a
     * keystroke that landed while the save was in flight.
     */
    const seededFor = useRef<number | null>(null);
    useEffect(() => {
        if (templateId === null) {
            seededFor.current = null;
            return;
        }
        if (!template || template.id !== templateId || seededFor.current === templateId) return;
        seededFor.current = templateId;
        setTitle(template.title ?? '');
        setBody(template.body_md ?? '');
    }, [templateId, template]);

    const editable = template?.status === 'draft';
    const dirty =
        !!template && editable && (body !== (template.body_md ?? '') || title !== (template.title ?? ''));

    const debouncedBody = useDebounce(body, PREVIEW_DEBOUNCE_MS);

    const referenced = useMemo(() => collectMergeFields(debouncedBody), [debouncedBody]);
    const unresolvable = useMemo(() => unresolvableMergeFields(referenced), [referenced]);
    /**
     * The two warnings below are different defects and are shown differently on purpose.
     * MALFORMED is red because the braces print on the signed page and no data can fix it;
     * UNRESOLVABLE is amber because the field is spelled correctly and merges — to a blank —
     * until the merge query learns it.
     */
    const malformed = useMemo(() => malformedMergeFields(debouncedBody), [debouncedBody]);

    const save = useMutation({
        mutationFn: () =>
            updateTemplateDraft({
                templateId: template!.id,
                bodyMd: body,
                title: title.trim(),
            }),
        onSuccess: () => {
            toast.success('Draft saved');
            // The saved title is the trimmed one, so the form has to agree with it or the
            // unsaved-changes indicator never goes out after a title with a stray space.
            setTitle((current) => current.trim());
            queryClient.invalidateQueries({ queryKey: ['agreement-templates'] });
            queryClient.invalidateQueries({ queryKey: ['agreement-template-body', templateId] });
        },
        onError: (error) => {
            captureError(error);
            toast.error(parseAgreementError(error).message);
        },
    });

    const close = () => {
        setConfirmDiscard(false);
        onOpenChange(false);
    };

    const requestClose = () => {
        if (dirty) {
            setConfirmDiscard(true);
            return;
        }
        close();
    };

    const canSave = editable && dirty && title.trim().length > 0 && body.trim().length > 0;

    return (
        <>
            <Sheet open={!!templateId} onOpenChange={(open) => !open && requestClose()}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[92vw] !max-w-[1500px] rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader className="pr-8">
                        <SheetTitle className="flex flex-wrap items-center gap-2">
                            {template ? template.title : 'Template'}
                            {template && (
                                <Badge
                                    variant="outline"
                                    className={
                                        editable
                                            ? 'gap-1 border-amber-500/40 font-normal text-amber-700 dark:text-amber-400'
                                            : 'gap-1 font-normal'
                                    }
                                >
                                    {!editable && <Lock className="h-3 w-3" />}
                                    {editable ? 'Draft' : template.status === 'published' ? 'Published' : 'Retired'}{' '}
                                    v{template.version}
                                </Badge>
                            )}
                        </SheetTitle>
                        {template && (
                            <p className="text-sm text-muted-foreground">
                                <span className="font-mono">{template.template_key}</span>
                                {template.effective_at &&
                                    ` · published ${format(new Date(template.effective_at), 'MMM d, yyyy')}`}
                                {!editable &&
                                    ' · sealed by the immutability trigger — a change is a new version'}
                            </p>
                        )}
                    </SheetHeader>

                    <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-b pb-3">
                        <div className="min-w-[16rem] flex-1">
                            <Label htmlFor="template-title" className="text-xs text-muted-foreground">
                                Document title
                            </Label>
                            <Input
                                id="template-title"
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                                readOnly={!editable}
                                disabled={!editable}
                                className="mt-1"
                            />
                        </div>
                        {editable && (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => save.mutate()}
                                    disabled={!canSave || save.isPending}
                                >
                                    {save.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Save className="mr-2 h-4 w-4" />
                                    )}
                                    Save draft
                                </Button>
                                <Button onClick={() => template && onPublish(template)} disabled={dirty}>
                                    <Upload className="mr-2 h-4 w-4" />
                                    Publish
                                </Button>
                            </div>
                        )}
                    </div>

                    {isLoading || !template ? (
                        <div className="mt-4 flex-1 space-y-3">
                            <Skeleton className="h-8 w-1/3" />
                            <Skeleton className="h-full min-h-[20rem] w-full" />
                        </div>
                    ) : (
                        <div className="mt-4 grid min-h-0 flex-1 gap-4 lg:grid-cols-2">
                            {/* ── Source ─────────────────────────────────────────────── */}
                            <div className="flex min-h-0 flex-col">
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                        Markdown source
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {referenced.length} merge field{referenced.length === 1 ? '' : 's'} ·{' '}
                                        {body.split('\n').length} lines
                                        {dirty && ' · unsaved changes'}
                                    </p>
                                </div>
                                <Textarea
                                    value={body}
                                    onChange={(event) => setBody(event.target.value)}
                                    readOnly={!editable}
                                    spellCheck={false}
                                    aria-label="Template markdown source"
                                    className="min-h-0 flex-1 resize-none font-mono text-xs leading-relaxed"
                                />
                                {malformed.length > 0 && (
                                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                                        <OctagonAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        <span>
                                            <span className="font-medium">
                                                Not a merge field: <span className="font-mono">{malformed.join(', ')}</span>.
                                            </span>{' '}
                                            The document generator matches{' '}
                                            <span className="font-mono">{'{{driver.full_name}}'}</span> exactly — no
                                            spaces inside the braces, and each part between the dots starts with a
                                            letter or an underscore and carries only letters, digits and
                                            underscores after it. One{' '}
                                            <span className="font-mono">{'{{'}</span> and one{' '}
                                            <span className="font-mono">{'}}'}</span> per field and no brace either
                                            side of them:{' '}
                                            <span className="font-mono">{'{{{driver.full_name}}}'}</span> prints the
                                            outer pair on the page. Anything else is copied onto the signed page as
                                            you see it here, braces included. The draft still saves; publishing is
                                            refused until this is fixed.
                                        </span>
                                    </div>
                                )}
                                {unresolvable.length > 0 && (
                                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                                        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        <span>
                                            No merge data supplies{' '}
                                            <span className="font-mono">{unresolvable.join(', ')}</span> — these print
                                            as blanks on every document until the merge query supplies them.
                                        </span>
                                    </div>
                                )}
                                {editable && (
                                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                                        Rewording, reordering and reformatting are yours, and so is any field the
                                        preview already resolves. Introducing a{' '}
                                        <span className="font-mono">{'{{field}}'}</span> this system does not have
                                        yet still needs engineering: the merge query has to learn to supply it, and
                                        this editor does not update the pre-flight list that stops a document being
                                        sent with a required field still blank.
                                    </p>
                                )}
                            </div>

                            {/* ── Preview ────────────────────────────────────────────── */}
                            <div className="flex min-h-0 flex-col">
                                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    Preview
                                </p>
                                <div className="mb-2 rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground">Specimen data.</span> Every
                                    tinted value below is invented sample content — no driver, vehicle, contract
                                    or amount on this page is real. A live document merges the contract's own
                                    values at generation time.
                                </div>
                                <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border bg-background p-4">
                                    <AgreementMarkdown
                                        renderedMd=""
                                        values={SPECIMEN_MERGE_DATA}
                                        parties={template.parties ?? []}
                                        templateBody={debouncedBody}
                                        highlight
                                        className="mx-auto max-w-3xl"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This draft has edits that were never saved. Closing now loses them — the version in
                            the database keeps the wording it had at your last save.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep editing</AlertDialogCancel>
                        <AlertDialogAction onClick={close}>Discard</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
